import { Box, BoxProps } from '@hover/blueprint';
import React, { useEffect, useRef, useState } from 'react';
import PauseVideoButton from '../../../../assets/svgs/PauseVideoButton';
import PlayVideoButton from '../../../../assets/svgs/PlayVideoButton';
import HomePageVideo from '../../../../assets/videos/Hero_v2.mp4';

const VIDEO_DURATION_FALLBACK = 20.032;

type HTMLVideoMetadataCallback = Parameters<
  HTMLVideoElement['addEventListener']
>[1];

interface Props extends BoxProps {
  onPlay: () => void;
  onPause: () => void;
  onEnded: () => void;
  setVideoDuration: (videoDuration: number) => void;
}

const HeroVideo: React.FC<Props> = ({
  onPause,
  onPlay,
  onEnded,
  setVideoDuration,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const [isPlaying, setIsPlaying] = useState(false);

  const onClick = (pause: boolean) => {
    if (videoRef.current) {
      if (pause) {
        onPause();
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        onPlay();
        void videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  useEffect(() => {
    if (videoRef.current) {
      const video = videoRef.current;

      const onLoadedMetadata: HTMLVideoMetadataCallback = event => {
        setVideoDuration(
          event.currentTarget instanceof HTMLVideoElement
            ? event.currentTarget.duration
            : VIDEO_DURATION_FALLBACK,
        );
      };

      if (video.duration) {
        setVideoDuration(video.duration);
      } else {
        video.addEventListener('loadedmetadata', onLoadedMetadata);

        return () =>
          video.removeEventListener('loadedmetadata', onLoadedMetadata);
      }
    } else {
      // eslint-disable-next-line no-console
      console.error('HeroVideo: unable to determine video duration');
    }

    return undefined;
  }, [setVideoDuration]);

  return (
    <Box
      maxW={{ base: '100%', desktop: '1015px' }}
      minH={{ base: '300px', tablet: '600px', desktop: '700px' }}
      w="100vw"
      overflow="hidden"
      borderBottomLeftRadius="40%"
      position="relative"
    >
      <Box
        d={{ base: 'flex', tablet: 'none' }}
        opacity={0.5}
        position="absolute"
        top={0}
        left={0}
        w="100%"
        h="100%"
        zIndex={100}
        alignItems="center"
        justifyContent="center"
      >
        <Box d={isPlaying ? 'none' : 'block'} onClick={() => onClick(false)}>
          <PlayVideoButton />
        </Box>
        <Box d={isPlaying ? 'block' : 'none'} onClick={() => onClick(true)}>
          <PauseVideoButton />
        </Box>
      </Box>
      <video
        ref={videoRef}
        onPlay={onPlay}
        onPause={onPause}
        onEnded={onEnded}
        style={{ height: '100%', objectFit: 'cover' }}
        id="hero-video"
        width="100%"
        muted
        playsInline
      >
        <source src={HomePageVideo} type="video/mp4" />
      </video>
    </Box>
  );
};

export default HeroVideo;
